import {
  Component,
  computed,
  effect,
  EventEmitter,
  input,
  OnInit,
  Output,
  signal,
  Signal,
  WritableSignal,
  inject,
} from '@angular/core';
import { HttpParams } from '@angular/common/http';
import {
  FilterButtonComponent,
  FilterGroup,
  FilterOption,
} from '../../../../../shared/components/filter-button/filter-button.component';
import { ActivatedRoute, Router } from '@angular/router';
import { arrayStringToNumber, updateParamsObjectToPageOne } from '../../../../../shared/utils/filter-utils';
import { TransactionFilterParams, TransactionStatusLabel } from '../../../../../_interfaces/transactions.interface';
import { BoFormsModule } from '@bo/ng-forms';
import { NgbDateParserFormatter, NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { CategorySelectMultiComponent } from '../../../../../shared/components/forms/category-select-multi/category-select-multi.component';
import { EmployeeSelectMultiComponent } from '../../../../../shared/components/forms/employee-select-multi/employee-select-multi.component';
import { generateTransactionFilterGroups } from '../_utils/transactions-utils';
import { DateRangePickerComponent } from '../../../../../shared/components/forms/date-range-picker/date-range-picker.component';
import { formatDateSlash } from '../../../../../shared/utils/general-utils';
import { ContactsSelectComponent } from '../../../../../shared/components/forms/contacts-select/contacts-select.component';
import { Store } from '@ngxs/store';
import { SubscriptionSelectors } from '../../../../../store/subscription/subscription.selectors';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-transaction-filter',
  imports: [
    FilterButtonComponent,
    BoFormsModule,
    NgSelectModule,
    CategorySelectMultiComponent,
    EmployeeSelectMultiComponent,
    DateRangePickerComponent,
    ContactsSelectComponent,
  ],
  templateUrl: './transaction-filter.component.html',
})
export class TransactionFilterComponent implements OnInit {
  readonly store = inject(Store);
  readonly router = inject(Router);
  readonly route = inject(ActivatedRoute);
  readonly ngbDateParserFormatter = inject(NgbDateParserFormatter);

  readonly hasAccounting$ = this.store.select(SubscriptionSelectors.hasAccounting);
  protected readonly hasAccounting = toSignal(this.hasAccounting$, { initialValue: false });
  @Output() query: EventEmitter<HttpParams> = new EventEmitter<HttpParams>();
  isDepositView = input<boolean>();
  resetHandled = signal(false);

  protected filterParams: Signal<TransactionFilterParams> = computed(() => {
    const queryParams = this.route.snapshot.queryParams;
    return {
      status: queryParams['status']?.split(',') ?? [],
      receipt: queryParams['receipt'] ?? '',
      date_from: queryParams['date_from'] ?? '',
      date_to: queryParams['date_to'] ?? '',
      contact: queryParams['contact'] ?? '',
      category: queryParams['category']?.split(',') ?? [],
      employee: queryParams['employee']?.split(',') ?? [],
    };
  });

  protected hasDateFilters: Signal<boolean> = computed(() => {
    return !!this.dateFrom() || !!this.dateTo();
  });
  protected hasContactFilter: Signal<boolean> = computed(() => {
    return !!this.selectedContact();
  });
  protected hasCategoryFilters: Signal<boolean> = computed(() => {
    return !!this.selectedCategories().length;
  });
  protected hasEmployeeFilters: Signal<boolean> = computed(() => {
    return !!this.selectedEmployees().length;
  });
  protected dateFrom: WritableSignal<NgbDateStruct | null> = signal(null);
  protected dateTo: WritableSignal<NgbDateStruct | null> = signal(null);

  protected filterGroup: Array<FilterGroup> = [];

  protected selectedContact: WritableSignal<string | undefined> = signal(undefined);
  protected selectedCategories: WritableSignal<number[]> = signal([]);
  protected selectedEmployees: WritableSignal<string[]> = signal([]);

  private queryParams = new HttpParams();

  constructor() {
    effect(() => {
      // these query params where removed on switching to deposit view, need to update the template view to reflect.
      if (!this.isDepositView()) {
        this.selectedCategories.set([]);
        this.selectedEmployees.set([]);
        this.selectedContact.set(undefined);
      }
    });
  }

  ngOnInit(): void {
    const statusQuery: string[] = this.filterParams().status ?? [];
    const receiptQuery: string = this.filterParams().receipt ?? '';
    const dateFromQuery: string = this.filterParams().date_from ?? '';
    const dateToQuery: string = this.filterParams().date_to ?? '';

    // set custom filter values here.
    this.dateFrom.set(this.ngbDateParserFormatter.parse(dateFromQuery));
    this.dateTo.set(this.ngbDateParserFormatter.parse(dateToQuery));
    this.selectedCategories.set(arrayStringToNumber(this.filterParams().category ?? []));
    this.selectedContact.set(this.filterParams().contact || undefined);
    this.selectedEmployees.set(this.filterParams().employee ?? []);

    // Set the checkbox section here
    this.filterGroup = generateTransactionFilterGroups(receiptQuery, statusQuery);
  }

  onFilter(options: Array<FilterOption>) {
    // create "clean" query params, otherwise historical queries gets added again.
    const existingParams = this.route.snapshot.queryParams;
    this.queryParams = new HttpParams().appendAll(existingParams);

    // process and add new queries
    this.processCheckboxQueries(options);
    this.processDateQueries();
    this.processCategoryQueries();
    this.processEmployeeQueries();
    this.processContactQueries();

    // Navigate with updated filters and reset the page to 1
    this.router
      .navigate([], {
        relativeTo: this.route,
        queryParams: updateParamsObjectToPageOne(this.queryParams),
      })
      .then(() => {});
  }

  processCheckboxQueries(options: Array<FilterOption>) {
    const checkedOptions = options.filter((opt) => opt.checked);
    const statusQuery: TransactionStatusLabel[] = [];
    let receiptQuery: boolean | undefined;

    if (checkedOptions.length > 0) {
      // Process selected options
      checkedOptions.forEach((opt) => {
        if (opt.value === true || opt.value === false) {
          receiptQuery = opt.value;
        } else {
          statusQuery.push(opt.value as TransactionStatusLabel);
        }
      });

      // Add status and receipt filters
      if (statusQuery.length > 0) {
        this.queryParams = this.queryParams.set('status', statusQuery.toString());
      } else {
        this.queryParams = this.queryParams.delete('status');
      }

      if (receiptQuery === undefined) {
        this.queryParams = this.queryParams.delete('receipt');
      } else {
        this.queryParams = this.queryParams.set('receipt', receiptQuery.toString());
      }
    } else {
      // Clear checkedOptions from filters
      this.queryParams = this.queryParams.delete('status');
      this.queryParams = this.queryParams.delete('receipt');
    }
  }

  setFromDate(dateString: string | null) {
    this.dateFrom.set(dateString ? this.ngbDateParserFormatter.parse(formatDateSlash(dateString)) : null);
    if (dateString) this.dateTo.set(null);
  }

  setToDate(dateString: string | null) {
    this.dateTo.set(dateString ? this.ngbDateParserFormatter.parse(formatDateSlash(dateString)) : null);
  }

  processDateQueries() {
    // Add date filters
    if (this.dateFrom()) {
      this.queryParams = this.queryParams.set('date_from', this.ngbDateParserFormatter.format(this.dateFrom()));
    } else {
      this.queryParams = this.queryParams.delete('date_from');
    }

    if (this.dateTo()) {
      this.queryParams = this.queryParams.set('date_to', this.ngbDateParserFormatter.format(this.dateTo()));
    } else {
      this.queryParams = this.queryParams.delete('date_to');
    }
  }

  updateContact(event: string | undefined) {
    this.selectedContact.set(event);
  }

  updateCategories(event: number[]) {
    this.selectedCategories.set(event);
  }

  updateEmployees(event: string[]) {
    this.selectedEmployees.set(event);
  }

  processCategoryQueries() {
    if (this.selectedCategories().length > 0) {
      this.queryParams = this.queryParams.set('category', this.selectedCategories().toString());
    } else {
      this.queryParams = this.queryParams.delete('category'); // Remove if no category
    }
  }

  processEmployeeQueries() {
    if (this.selectedEmployees().length) {
      this.queryParams = this.queryParams.set('employee', this.selectedEmployees().toString());
    } else {
      this.queryParams = this.queryParams.delete('employee'); // Remove if no category
    }
  }

  processContactQueries() {
    if (this.selectedContact()) {
      this.queryParams = this.queryParams.set('contact', this.selectedContact() ?? '');
    } else {
      this.queryParams = this.queryParams.delete('contact'); // Remove if no category
    }
  }

  clearDateFrom(): void {
    this.dateFrom.set(null);
  }

  clearDateTo(): void {
    this.dateTo.set(null);
  }

  handleReset(): void {
    // Resets the date picker fields on clicking the reset button
    this.clearDateFrom();
    this.clearDateTo();
    this.selectedCategories.set([]);
    this.selectedEmployees.set([]);
    this.selectedContact.set(undefined);
    this.resetHandled.set(true);
  }
}
